<script setup>
// PWA: registra o service worker (vite-plugin-pwa, ver vite.config.js) e
// mostra dois avisos:
//  - "Nova versão disponível": o service worker não troca a versão sozinho
//    (registerType 'prompt') — o usuário atualiza quando quiser, sem
//    perder um cadastro no meio. Checa versão nova a cada hora.
//  - "Sem conexão": o app depende de dado ao vivo; o service worker só
//    guarda os arquivos do app, nunca resposta da API.
//
// Só registra em produção — o import do módulo virtual é dinâmico, então dev
// e testes nem carregam o service worker.
import { ref, onMounted, onUnmounted } from 'vue'

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000

const needRefresh = ref(false)
const updating = ref(false)
const offline = ref(typeof navigator !== 'undefined' && navigator.onLine === false)

let updateServiceWorker = null
let updateTimer = null

const setOnline = () => { offline.value = false }
const setOffline = () => { offline.value = true }

onMounted(async () => {
  window.addEventListener('online', setOnline)
  window.addEventListener('offline', setOffline)

  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return
  try {
    const { registerSW } = await import('virtual:pwa-register')
    updateServiceWorker = registerSW({
      onNeedRefresh() { needRefresh.value = true },
      onRegisteredSW(_url, registration) {
        if (!registration) return
        updateTimer = setInterval(() => registration.update(), UPDATE_CHECK_INTERVAL_MS)
      },
    })
  } catch (err) {
    // Sem service worker o app funciona normalmente, só não fica instalável.
    console.warn('Falha ao registrar o service worker:', err)
  }
})

onUnmounted(() => {
  window.removeEventListener('online', setOnline)
  window.removeEventListener('offline', setOffline)
  if (updateTimer) clearInterval(updateTimer)
})

async function applyUpdate() {
  if (!updateServiceWorker) return
  updating.value = true
  // Ativa o service worker novo e recarrega a página com a versão nova.
  await updateServiceWorker(true)
}
</script>

<template>
  <!-- Sem conexão: faixa no topo, abaixo do botão do menu no mobile. -->
  <Transition name="pwa-fade">
    <div
      v-if="offline"
      role="status"
      class="fixed top-3 left-1/2 -translate-x-1/2 z-[1001] flex items-center gap-2 rounded-full bg-zinc-900/95 dark:bg-zinc-800/95 px-4 py-2 text-xs font-medium text-white shadow-lg"
    >
      <span class="w-2 h-2 rounded-full bg-danger-500 shrink-0" aria-hidden="true" />
      Sem conexão — os dados podem estar desatualizados
    </div>
  </Transition>

  <!-- Nova versão: mesmo visual do aviso de privacidade (App.vue). -->
  <Transition name="pwa-fade">
    <div
      v-if="needRefresh"
      role="alert"
      class="fixed bottom-4 left-1/2 z-[1000] w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 rounded-2xl border border-zinc-200 bg-white/95 px-4 py-3 shadow-2xl backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95 mb-[env(safe-area-inset-bottom)]"
    >
      <div class="flex items-center gap-3">
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-zinc-900 dark:text-white">Nova versão do Avance disponível</p>
          <p class="text-xs text-zinc-500 dark:text-zinc-400">Atualize para usar a versão mais recente.</p>
        </div>
        <button type="button" class="text-sm font-medium text-zinc-500 dark:text-zinc-400 px-2 py-2 shrink-0" @click="needRefresh = false">
          Depois
        </button>
        <button
          type="button"
          class="rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-60 shrink-0"
          :disabled="updating"
          @click="applyUpdate"
        >
          {{ updating ? 'Atualizando…' : 'Atualizar' }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.pwa-fade-enter-active,
.pwa-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.pwa-fade-enter-from,
.pwa-fade-leave-to { opacity: 0; }
</style>
